import { makeStyles } from '@material-ui/core/styles'

import palette from 'theme/palette'

export const EXPANDED_CONTAINER_PADDING = 12

export const useDailyGoalsTableStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    textAlign: 'left',
    '--daily-goal-row-height': '20px'
  },
  expandedContainer: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal - 1,
    width: '100vw',
    height: '100vh',
    padding: EXPANDED_CONTAINER_PADDING,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    boxSizing: 'border-box',
    '--daily-goal-row-height': 'var(--daily-goal-expanded-row-height)',
    '& $tableViewport': {
      flex: 1,
      minHeight: 0
    },
    '& $tableFrame': {
      maxHeight: '100%',
      overflowY: 'hidden'
    },
    '& $expandedScrollableTableFrame': {
      overflowY: 'auto'
    },
    '& $dateCell': {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(0.75)}px !important`
    },
    '& $input': {
      padding: theme.spacing(0.5, 0.75)
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    color: '#65747b',
    fontSize: 12,
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column'
    }
  },
  toolbarHints: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  toolbarHint: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    lineHeight: '14px',
    '& svg': {
      fontSize: 14
    }
  },
  toolbarButton: {
    height: 28,
    padding: theme.spacing(0.5, 1),
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    border: '1px solid #d9dfe3',
    borderRadius: 5,
    background: '#fff',
    color: '#465961',
    fontFamily: 'Roboto, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      borderColor: palette.primary.main,
      background: '#f4f5fd',
      color: palette.primary.main
    },
    '&:disabled': {
      borderColor: '#e3e7e9',
      background: '#f7f8f9',
      color: '#aab4b9',
      cursor: 'default'
    },
    '& svg': {
      fontSize: 16
    }
  },
  tableViewport: {
    width: '100%',
    minHeight: 0
  },
  tableFrame: {
    position: 'relative',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    border: '1px solid #dfe4e8',
    borderRadius: 8,
    background: '#fff',
    boxShadow: '0 5px 18px rgba(38, 50, 56, 0.05)',
    outline: 'none'
  },
  disabledTableFrame: {
    overflow: 'hidden !important'
  },
  expandedScrollableTableFrame: {
    overflowY: 'auto'
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    minWidth: '100%',
    tableLayout: 'fixed',
    userSelect: 'none',
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      minWidth: 108,
      maxWidth: 108,
      height: 40,
      padding: theme.spacing(0.5, 0.75),
      borderRight: '1px solid #dfe4e8',
      borderBottom: '1px solid #d5dce0',
      background: '#f4f7f8',
      color: '#33444c',
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.25,
      textAlign: 'left',
      verticalAlign: 'middle'
    },
    '& td': {
      minWidth: 108,
      maxWidth: 108,
      height: 'var(--daily-goal-row-height)',
      padding: 0,
      borderRight: '1px solid #e4e8eb',
      borderBottom: '1px solid #e4e8eb',
      background: '#fff'
    },
    '& tbody tr:nth-child(even):not([data-daily-goal-total-row]) > td': {
      background: '#f7f9fa'
    }
  },
  dateHeader: {
    position: 'sticky' as const,
    left: 0,
    zIndex: 4,
    minWidth: '76px !important',
    maxWidth: '76px !important',
    background: '#e9eff1 !important'
  },
  dateCell: {
    position: 'sticky',
    left: 0,
    top: 'auto',
    zIndex: 1,
    height: 'var(--daily-goal-row-height)',
    minWidth: '76px !important',
    maxWidth: '76px !important',
    padding: `1px ${theme.spacing(0.75)}px !important`,
    borderRight: '1px solid #e4e8eb',
    borderBottom: '1px solid #e4e8eb',
    background: '#f7f9fa !important',
    color: '#52636b',
    fontSize: 11,
    fontWeight: 700,
    textAlign: 'left',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box'
  },
  weekdayLabel: {
    color: '#89979d',
    fontSize: 10,
    fontWeight: 600
  },
  totalRow: {
    '& th, & td': {
      height: 40,
      padding: `${theme.spacing(0.5)}px ${theme.spacing(0.75)}px !important`,
      borderRight: '1px solid #dfe4e8',
      borderBottom: '1px solid #d5dce0',
      background: '#f4f7f8 !important',
      color: '#33444c',
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.25,
      verticalAlign: 'middle',
      fontVariantNumeric: 'tabular-nums'
    },
    '& th': {
      position: 'sticky',
      left: 0,
      top: 'auto',
      zIndex: 1,
      minWidth: '76px !important',
      maxWidth: '76px !important',
      background: '#e9eff1 !important',
      textAlign: 'left'
    },
    '& td': {
      textAlign: 'right'
    }
  },
  cell: {
    position: 'relative'
  },
  selectedCell: {
    background: '#eef0fc !important'
  },
  input: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(0.125, 0.75),
    border: 0,
    outline: 0,
    background: 'transparent',
    color: '#263238',
    fontFamily: 'Roboto, sans-serif',
    fontSize: 12,
    textAlign: 'right',
    boxSizing: 'border-box',
    touchAction: 'none',
    userSelect: 'text',
    '&:focus': {
      outline: `2px solid ${palette.primary.main}`,
      outlineOffset: -2
    },
    '&::placeholder': {
      color: '#adb8bd'
    }
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexWrap: 'wrap',
      justifyContent: 'flex-start'
    }
  },
  orderDialog: {
    borderRadius: 10
  },
  orderDialogTitle: {
    padding: theme.spacing(2.25, 2.5, 0.75),
    color: '#263238',
    '& h2': {
      fontSize: 18,
      fontWeight: 700
    }
  },
  orderDialogContent: {
    padding: theme.spacing(0, 2.5, 1.5)
  },
  orderDialogDescription: {
    margin: theme.spacing(0, 0, 1.5),
    color: '#65747b',
    fontSize: 12,
    lineHeight: 1.45
  },
  storeOrderList: {
    maxHeight: 360,
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    overflowY: 'auto',
    listStyle: 'none'
  },
  storeOrderItem: {
    minHeight: 44,
    padding: theme.spacing(0.5, 0.75),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: '1px solid #e0e5e8',
    borderRadius: 7,
    background: '#fff',
    boxSizing: 'border-box',
    cursor: 'grab',
    transition: 'border-color 120ms ease, background 120ms ease, opacity 120ms ease',
    '&:hover': {
      borderColor: '#c4ccd1',
      background: '#f8fafb'
    },
    '&:active': {
      cursor: 'grabbing'
    }
  },
  draggedStoreOrderItem: {
    borderColor: `${palette.primary.main} !important`,
    background: '#f4f5fd !important',
    opacity: 0.72
  },
  dragTargetStoreOrderItem: {
    borderColor: `${palette.primary.main} !important`,
    boxShadow: `inset 0 0 0 1px ${palette.primary.main}`
  },
  dragHandle: {
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    color: '#9aa7ad',
    '& svg': {
      fontSize: 20
    }
  },
  storeOrderPosition: {
    width: 24,
    height: 24,
    display: 'inline-grid',
    flex: '0 0 auto',
    placeItems: 'center',
    borderRadius: 4,
    background: '#eef2f4',
    color: '#607078',
    fontSize: 11,
    fontWeight: 700
  },
  storeOrderName: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    color: '#33444c',
    fontSize: 13,
    fontWeight: 600,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  storeOrderControls: {
    display: 'inline-flex',
    gap: theme.spacing(0.25)
  },
  storeOrderButton: {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-grid',
    placeItems: 'center',
    border: 0,
    borderRadius: 4,
    background: 'transparent',
    color: '#607078',
    cursor: 'pointer',
    '&:hover:not(:disabled)': {
      background: '#e9edef',
      color: palette.primary.main
    },
    '&:disabled': {
      color: '#c7cfd3',
      cursor: 'default'
    },
    '& svg': {
      fontSize: 17
    }
  },
  orderDialogActions: {
    padding: theme.spacing(1.25, 2.5, 2.25),
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
      '& $restoreOrderButton': {
        width: '100%',
        margin: `0 0 ${theme.spacing(0.5)}px !important`,
        justifyContent: 'center'
      }
    }
  },
  restoreOrderButton: {
    marginRight: 'auto',
    padding: theme.spacing(0.75, 1),
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    border: 0,
    borderRadius: 5,
    background: 'transparent',
    color: palette.primary.main,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      background: '#f4f5fd'
    },
    '&:disabled': {
      background: 'transparent',
      color: '#b7c0c5',
      cursor: 'default'
    },
    '& svg': {
      fontSize: 17
    }
  },
  cancelOrderButton: {
    padding: theme.spacing(0.75, 1.5),
    border: 0,
    borderRadius: 5,
    background: 'transparent',
    color: '#607078',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      background: '#f1f4f5'
    }
  },
  applyOrderButton: {
    padding: theme.spacing(0.75, 1.5),
    border: `1px solid ${palette.primary.main}`,
    borderRadius: 5,
    background: palette.primary.main,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(0.94)'
    }
  }
}))
